import {Box, Button, createListCollection, Field, Heading, Input, Portal, Select, Show, Stack, Table, Text} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {dataApi, Template as Temp} from "../../api/data-api";
import {DateTime} from "luxon";
import {useToast} from "@chakra-ui/toast";
import {arrayBufferToBase64, base64ToArrayBuffer, downloadLinkProcessor, openFile} from "../../components/hooks/FileLoader";
import {AxiosResponse} from "axios";
import {errorNotify} from "../../components/hooks/CommonUtils";

export interface TemplateExt {
    id: number | null,
    name: string,
    type: string,
    file: File | null,
}

export const templateTypeList = createListCollection({
    items: [
        {value: "PAYOUT_LIST", label: "Payout list"},
        {value: "CONTRACT", label: "Contract"},
    ],
    itemToString: (item: any) => `${item.label}`,
    itemToValue: (item: any) => item.value,
});

export const Template = () => {
    const [items, setItems] = useState<Temp[]>([]);
    const toast = useToast();
    const [showForm, setShowForm] = useState<boolean>(false);
    const inputDefault: TemplateExt = useMemo(() => {
        return {
            id: null,
            name: "",
            type: "PAYOUT_LIST",
            file: null,
        }
    }, []);
    const [inputParams, setInputParams] = useState<TemplateExt>(inputDefault);

    useEffect(() => {
        dataApi.templateOwnList()
            .then(o => {
                if (o)
                    setItems(o);
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const templateDelete = useCallback((it: Temp) => {
        if (!it)
            return;
        dataApi.templateDelete(it.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== it.ID);
                    })
                } else {
                    toast({title: "Delete failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const prepareInput = useCallback((item: Temp | null) => {
        setInputParams({
            id: item ? item.ID : null,
            name: item ? item.NAME : "",
            type: item ? item.TYPE1 : "PAYOUT_LIST",
            file: null,
        });
    }, []);

    const createOrUpdate = useCallback(() => {
        if (!inputParams.file)
            return;

        var reader = new FileReader();
        reader.onload = function (e2) {
            var contents = e2.target?.result;
            // const arrayBuffer = reader.result;
            if (contents && contents instanceof ArrayBuffer) {
                const content = arrayBufferToBase64(contents);
                if (content) {
                    dataApi.templateCreateOrUpdate(inputParams.id, inputParams.name, inputParams.type, content)
                        .then((value) => {
                            toast({title: "Save", status: "success"});
                            setItems(prev => {
                                let findO = prev.find(o => o.ID === value.ID);
                                const lst = [...prev];
                                if (findO) {
                                    lst[prev.indexOf(findO)] = value;
                                } else {
                                    lst.push(value);
                                }
                                return lst;
                            });
                            if (!inputParams.id) {
                                setInputParams(prev => {
                                    return {...prev, id: value.ID};
                                });
                            }
                            prepareInput(null);
                            setShowForm(false);
                        })
                        .catch((err) => errorNotify(err, toast));
                }
            }
        };
        reader.readAsArrayBuffer(inputParams.file);
    }, [inputParams, toast, prepareInput]);

    const fileDownload = useCallback((it: Temp) => {
        if (!it)
            return;
        dataApi.templateGet(it.ID)
            .then(o => {
                if (!o.CONTENT)
                    return;
                downloadLinkProcessor(it.NAME, {data: base64ToArrayBuffer(o.CONTENT)} as AxiosResponse);
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    return (
        <Stack width="full" gap="5">
            <Heading size="xl">Templates</Heading>
            <Box>
                <Button onClick={e => {
                    if (showForm) {
                        setShowForm(false);
                    } else {
                        prepareInput(null);
                        setShowForm(true);
                    }
                }}>
                    Создать новый
                </Button>
            </Box>
            <Show when={showForm}>
                <Box borderWidth={1} borderColor="gray">
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>name <Field.RequiredIndicator/></Field.Label>
                        <Input type="text" placeholder="name"
                               value={inputParams.name}
                               onChange={(it) => setInputParams((prev) => {
                                   return {...prev, name: it.target.value}
                               })}/>
                    </Field.Root>
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>type <Field.RequiredIndicator/></Field.Label>
                        <Select.Root
                            value={[String(inputParams.type)]}
                            // defaultValue={[inputParams.type]}
                            collection={templateTypeList}
                            onValueChange={(e: any) => setInputParams(prev => {
                                return {
                                    ...prev, type: e.value[0]
                                }
                            })}>
                            <Select.HiddenSelect/>
                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder="type"/>
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator/>
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                                <Select.Positioner>
                                    <Select.Content>
                                        {templateTypeList.items.map((it: any) => (
                                            <Select.Item item={it} key={it.value}>
                                                {it.label}
                                                <Select.ItemIndicator/>
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>
                    </Field.Root>
                    <Field.Root orientation="horizontal" required>
                        <Field.Label>File loader <Field.RequiredIndicator/></Field.Label>
                        <Text>{inputParams.file?.name}</Text>
                        <Button borderRadius={0}
                                onClick={e => {
                                    openFile(null, file => {
                                        setInputParams((prev) => {
                                            return {...prev, file: file, name: file.name};
                                        });
                                    })
                                }}>
                            Load
                        </Button>
                    </Field.Root>
                    <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                            onClick={e => createOrUpdate()}>
                        {inputParams.id ? "Сохранить" : "Создать"}
                    </Button>
                </Box>
            </Show>
            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Имя</Table.ColumnHeader>
                        <Table.ColumnHeader>Тип</Table.ColumnHeader>
                        <Table.ColumnHeader>Создан</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.NAME}</Table.Cell>
                            <Table.Cell>{item.TYPE1}</Table.Cell>
                            <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => fileDownload(item)}>
                                    Download
                                </Button>
                                <Button onClick={e => {
                                    prepareInput(item);
                                    setShowForm(true);
                                }}>
                                    Edit
                                </Button>
                                <Button onClick={e => templateDelete(item)}>
                                    Delete
                                </Button>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

        </Stack>
    );
};